import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampoDeMoeda,
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  CampoDeTexto,
  Cartao,
  CartaoIndicador,
  CelulaDeTotal,
  EstadoVazio,
  Modal,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../componentes/ui';
import { useSimularPassivo } from '../hooks/useEscrita';
import { useCarteira, useClientesParaEscolher } from '../hooks/useOperacao';
import type {
  EntradaSimulacaoPassivo,
  ParcelaSimulada,
  ResultadoSimulacaoPassivo,
} from '../servicos/escrita';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { formatarCnpj, lerValorDigitado } from '../servicos/formato';
import { requisicaoAutenticada } from '../servicos/http';
import { diaLocal, type ColunaDaPlanilha } from '../servicos/planilha';
import { Pagina } from './Carteira';
import { ErroDaApi } from './ErroDaApi';
import { ModalDeEnvioPorEmail, type ResultadoDoEnvio } from './EnvioPorEmail';
import type { ContratoPassivo } from './PassivoContratos';
import { lerDefaults } from './passivo/configuracoes';
import { explicacaoDoSistema } from './passivo/sistema-de-amortizacao';
import {
  CampoDaFormaDePagamento,
  formaDePagamentoValida,
  useLeituraDaFormaDePagamento,
} from './passivo/FormaDePagamento';

/**
 * ICMS Passivo — simulação de renegociação de dívida.
 *
 * ## Esta tela é a fase 1 de três
 *
 * Simulação (aqui), contrato e checklist. As outras duas vivem em
 * **Contratos de dívida** — separadas porque a simulação não exige escolher
 * cliente nem fornecedor: ela responde "quanto economiza" com qualquer número
 * digitado, e é o que abre a conversa comercial.
 *
 * A simulação em si não é persistida. O que fecha o ciclo é **Fechar
 * negociação**: depois do resultado, o usuário escolhe cliente, fornecedor e
 * dia de vencimento, e o contrato nasce já ativo com os números que estão na
 * tela — sem redigitar nada na outra tela (item 33). Quem preferir deixar como
 * rascunho tem "Criar como simulação" no mesmo modal.
 *
 * ## Os números do contrato são os da simulação, não os do formulário
 *
 * O formulário continua editável depois de simular. Se o contrato lesse os
 * campos, bastaria mexer no deságio depois de simular para fechar um acordo
 * com parcelas que ninguém viu. Por isso o modal recebe `simular.variables` —
 * a entrada que gerou o resultado — e o botão só habilita enquanto o formulário
 * ainda bate com ela.
 *
 * ## Nenhuma conta acontece aqui
 *
 * Price, SAC, deságio e economia vêm prontos de `POST /passivo/simulacoes`, que
 * só traduz o que o domínio calculou. A única conta desta tela é
 * `lerValorDigitado`, que converte "1.234,56" no número do corpo da requisição.
 */
export function Passivo(): JSX.Element {
  const navegar = useNavigate();
  const simular = useSimularPassivo();
  const [fechando, setFechando] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  // Os valores iniciais vêm de Configurações › Simulação (preferência do
  // navegador — ver `passivo/configuracoes.ts`); sem nada gravado, os de fábrica.
  const [form, setForm] = useState({ valorDivida: '', ...lerDefaults() });

  const lerEntrada = (): EntradaSimulacaoPassivo | null => {
    const valorDivida = lerValorDigitado(form.valorDivida);
    if (valorDivida === null) return null;

    return {
      valorDivida,
      percentualDesagio: Number(form.percentualDesagio),
      quantidadeParcelas: Number(form.quantidadeParcelas),
      taxaJurosMensal: Number(form.taxaJurosMensal),
      indice: form.indice,
    };
  };

  const enviar = (): void => {
    const entrada = lerEntrada();
    if (entrada !== null) simular.mutate(entrada);
  };

  // Limpa só a dívida: deságio, prazo e taxa são a proposta comercial, e quem
  // simula várias dívidas seguidas quer a mesma proposta para todas.
  const novaSimulacao = (): void => {
    simular.reset();
    setForm((f) => ({ ...f, valorDivida: '' }));
  };

  const aoFecharNegociacao = (contrato: ContratoPassivo): void => {
    setFechando(false);
    simular.reset();
    // A tela de contratos lê o endereço: chega com o cliente escolhido e o
    // contrato recém-criado aberto.
    navegar(`/passivo/contratos?cliente=${contrato.clienteId}&contrato=${contrato.id}`);
  };

  const r = simular.data;
  // A entrada que gerou `r`. `variables` só some no `reset()`, então enquanto
  // houver resultado ela existe — o `undefined` abaixo é só para o tipo.
  const entradaSimulada = simular.variables;
  const parametrosMudaram =
    r !== undefined &&
    entradaSimulada !== undefined &&
    !mesmaEntrada(entradaSimulada, lerEntrada());

  return (
    <Pagina
      titulo="ICMS Passivo"
      descricao="Simule a quitação de uma dívida de ICMS com crédito de terceiro, antes de montar o contrato."
    >
      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <Cartao como="section" titulo="Parâmetros">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
          >
            <CampoDeMoeda
              rotulo="Dívida de ICMS"
              required
              value={form.valorDivida}
              onChange={(texto) => setForm({ ...form, valorDivida: texto })}
            />
            <Campo
              rotulo="Deságio"
              numerico
              sufixo="%"
              ajuda="Percentual de desconto sobre o crédito comprado."
              value={form.percentualDesagio}
              onChange={(e) => setForm({ ...form, percentualDesagio: e.target.value })}
            />
            <Campo
              rotulo="Parcelas"
              numerico
              value={form.quantidadeParcelas}
              onChange={(e) => setForm({ ...form, quantidadeParcelas: e.target.value })}
            />
            <Campo
              rotulo="Taxa de juros mensal"
              numerico
              sufixo="%"
              ajuda="Entre 0 e 10."
              value={form.taxaJurosMensal}
              onChange={(e) => setForm({ ...form, taxaJurosMensal: e.target.value })}
            />
            <CampoDeSelecao
              rotulo="Sistema de amortização"
              ajuda={explicacaoDoSistema(form.indice, form.taxaJurosMensal)}
              opcoes={[
                { valor: 'sac', rotulo: 'SAC' },
                { valor: 'price', rotulo: 'Price' },
              ]}
              value={form.indice}
              onChange={(e) => setForm({ ...form, indice: e.target.value as 'sac' | 'price' })}
            />

            {simular.isError && (
              <ErroDaApi titulo="A simulação não foi feita" erro={simular.error} />
            )}

            <Botao type="submit" carregando={simular.isPending}>
              {simular.isPending ? 'Simulando…' : 'Simular'}
            </Botao>
          </form>
        </Cartao>

        <div className="flex min-w-0 flex-col gap-6">
          {/*
            Sem simulação, o quadrante da direita não fica com um aviso vazio:
            mostra as empresas já simuladas. Com resultado, o resultado toma o
            lugar e a lista desce para baixo da página.
          */}
          {r === undefined ? (
            <EmpresasSimuladas />
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <CartaoIndicador
                  rotulo="Dívida original"
                  valor={<ValorFiscal valor={r.valorDivida} tamanho="grande" />}
                />
                <CartaoIndicador
                  rotulo="Valor a pagar"
                  valor={<ValorFiscal valor={r.valorAPagar} tamanho="grande" />}
                  detalhe="Crédito comprado menos o deságio."
                />
                <CartaoIndicador
                  rotulo="Total parcelado"
                  valor={<ValorFiscal valor={r.valorTotal} tamanho="grande" />}
                  detalhe="Com juros, ao longo das parcelas."
                />
                <CartaoIndicador
                  rotulo="Economia"
                  valor={<ValorFiscal valor={r.economia} variante="economia" tamanho="grande" />}
                  detalhe={
                    <>
                      <ValorPercentual percentual={r.percentualEconomia} /> sobre a dívida original.
                    </>
                  }
                />
              </div>

              {r.economia < 0 && (
                <Aviso tom="atencao" titulo="Esta simulação custa mais que a dívida">
                  Com os juros aplicados, o total parcelado supera o valor original. Reduza o prazo,
                  a taxa, ou aumente o deságio.
                </Aviso>
              )}

              <TabelaDeParcelas parcelas={r.parcelas} total={r.valorTotal} />

              {parametrosMudaram && (
                <Aviso tom="atencao" titulo="Os parâmetros mudaram">
                  O resultado acima é da simulação anterior. Simule de novo para fechar a negociação
                  com os números atuais.
                </Aviso>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <BotaoExtrairXlsx
                  titulo="Parcelas simuladas - Passivo"
                  colunas={COLUNAS_DAS_PARCELAS}
                  linhas={r.parcelas}
                />
                <Botao variante="secundario" onClick={novaSimulacao}>
                  Nova simulação
                </Botao>
                <Botao
                  variante="secundario"
                  onClick={() => setEnviandoEmail(true)}
                  disabled={parametrosMudaram}
                >
                  Enviar por e-mail
                </Botao>
                <Botao onClick={() => setFechando(true)} disabled={parametrosMudaram}>
                  Fechar negociação
                </Botao>
              </div>
            </>
          )}
        </div>
      </div>

      {r !== undefined && <EmpresasSimuladas />}

      {enviandoEmail && entradaSimulada !== undefined && (
        <EnvioDaSimulacao entrada={entradaSimulada} aoFechar={() => setEnviandoEmail(false)} />
      )}

      {r !== undefined && entradaSimulada !== undefined && (
        <ModalDeFechamento
          entrada={entradaSimulada}
          resultado={r}
          aberto={fechando}
          aoFechar={() => setFechando(false)}
          aoConcluir={aoFecharNegociacao}
        />
      )}
    </Pagina>
  );
}

/* ─── Empresas simuladas ──────────────────────────────────────────────────── */

interface SimulacaoPassivo extends ContratoPassivo {
  readonly clienteNome: string;
  readonly clienteCnpj: string;
  readonly fornecedorNome: string;
  readonly criadoEm: string;
}

/**
 * Item 8 de 15/09 — quem já simulou nesta modalidade.
 *
 * Cada linha é um contrato em `SIMULACAO` ("Criar como simulação" no modal de
 * fechamento). A simulação avulsa do formulário acima não é gravada — é
 * conversa comercial; o que entra aqui é o que foi amarrado a um cliente.
 * Abrir leva ao contrato, na tela de Contratos, com o cliente já escolhido.
 */
const COLUNAS_DAS_SIMULACOES: readonly ColunaDaPlanilha<SimulacaoPassivo>[] = [
  { titulo: 'Empresa', largura: 44, valor: (s) => s.clienteNome },
  { titulo: 'CNPJ', largura: 20, valor: (s) => formatarCnpj(s.clienteCnpj) },
  { titulo: 'Fornecedor', largura: 40, valor: (s) => s.fornecedorNome },
  { titulo: 'Dívida', formato: 'moeda', valor: (s) => s.valorDividaOriginal },
  { titulo: 'Valor a pagar', formato: 'moeda', valor: (s) => s.valorFinalPagar },
  { titulo: 'Economia', formato: 'moeda', valor: (s) => s.economia },
  { titulo: 'Economia (%)', formato: 'percentual', valor: (s) => s.percentualEconomia },
  { titulo: 'Parcelas', largura: 10, valor: (s) => s.quantidadeParcelas },
  { titulo: 'Simulado em', formato: 'data', valor: (s) => diaLocal(s.criadoEm) },
];

function EmpresasSimuladas(): JSX.Element {
  const navegar = useNavigate();
  const simulacoes = useQuery({
    queryKey: ['passivo-simulacoes'],
    queryFn: () => requisicaoAutenticada<SimulacaoPassivo[]>('/passivo/simulacoes'),
  });

  const colunas: readonly ColunaTabela<SimulacaoPassivo>[] = [
    {
      chave: 'clienteNome',
      titulo: 'Empresa',
      larguraMinima: '18rem',
      celula: (s) => (
        <span className="flex flex-col">
          <span className="text-conteudo-primario">{s.clienteNome}</span>
          <span className="valor-fiscal text-xs text-conteudo-sutil">
            {formatarCnpj(s.clienteCnpj)}
          </span>
        </span>
      ),
    },
    {
      chave: 'fornecedorNome',
      titulo: 'Fornecedor',
      larguraMinima: '14rem',
      celula: (s) => <span className="text-sm text-conteudo-secundario">{s.fornecedorNome}</span>,
    },
    {
      chave: 'valorDividaOriginal',
      titulo: 'Dívida',
      numerica: true,
      celula: (s) => <ValorFiscal valor={s.valorDividaOriginal} />,
    },
    {
      chave: 'valorFinalPagar',
      titulo: 'Valor a pagar',
      numerica: true,
      celula: (s) => <ValorFiscal valor={s.valorFinalPagar} />,
    },
    {
      chave: 'economia',
      titulo: 'Economia',
      numerica: true,
      celula: (s) => <ValorFiscal valor={s.economia} variante="economia" />,
    },
    {
      chave: 'quantidadeParcelas',
      titulo: 'Parcelas',
      numerica: true,
      celula: (s) => <span className="valor-fiscal text-sm">{s.quantidadeParcelas}</span>,
    },
    {
      chave: 'criadoEm',
      titulo: 'Simulado em',
      larguraMinima: '8rem',
      celula: (s) => (
        <span className="valor-fiscal text-sm text-conteudo-secundario">
          {new Date(s.criadoEm).toLocaleDateString('pt-BR')}
        </span>
      ),
    },
  ];

  return (
    <Cartao
      como="section"
      titulo="Empresas simuladas"
      subtitulo="Simulações amarradas a um cliente e ainda não fechadas. Abrir leva ao contrato."
      acao={
        <BotaoExtrairXlsx
          titulo="Empresas simuladas - Passivo"
          tamanho="pequeno"
          disabled={simulacoes.data === undefined}
          colunas={COLUNAS_DAS_SIMULACOES}
          linhas={simulacoes.data ?? []}
        />
      }
    >
      {simulacoes.isError && (
        <ErroDaApi titulo="Não foi possível carregar as simulações" erro={simulacoes.error} />
      )}
      {!simulacoes.isPending && (simulacoes.data ?? []).length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma empresa simulada"
          descricao='Simule acima e use "Fechar negociação" › "Criar como simulação" para guardar a proposta de um cliente.'
        />
      ) : (
        <Tabela
          legenda="Empresas com simulação de Passivo"
          colunas={colunas}
          dados={simulacoes.data ?? []}
          chaveDaLinha={(s) => s.id}
          chaveDeCabecalhoDaLinha="clienteNome"
          carregando={simulacoes.isPending}
          aoClicarNaLinha={(s) =>
            navegar(`/passivo/contratos?cliente=${s.clienteId}&contrato=${s.id}`)
          }
        />
      )}
    </Cartao>
  );
}

function mesmaEntrada(a: EntradaSimulacaoPassivo, b: EntradaSimulacaoPassivo | null): boolean {
  return (
    b !== null &&
    a.valorDivida === b.valorDivida &&
    a.percentualDesagio === b.percentualDesagio &&
    a.quantidadeParcelas === b.quantidadeParcelas &&
    a.taxaJurosMensal === b.taxaJurosMensal &&
    a.indice === b.indice
  );
}

/* ─── Fechamento ──────────────────────────────────────────────────────────── */

/** Espelho de `criarContratoSchema` da API. */
interface EntradaContratoPassivo {
  readonly clienteId: string;
  readonly fornecedorId: string;
  readonly valorDividaOriginal: number;
  readonly valorCreditoComprado: number;
  readonly percentualDesagio: number;
  readonly quantidadeParcelas: number;
  readonly taxaJurosMensal: number;
  readonly diaVencimento: number;
  readonly indice: 'price' | 'sac';
  readonly observacoes?: string;
  readonly regraPagamentoTexto?: string;
  readonly ativarAoCriar?: boolean;
}

/**
 * Da simulação ao contrato: cliente, fornecedor, dia de vencimento.
 *
 * Os valores não são editáveis aqui de propósito — quem quer outro número volta
 * ao formulário e simula de novo. O que a tela mostrou é o que vai para o
 * contrato, inclusive o `indice`: no legado o corpo não o enviava e um contrato
 * simulado em Price nascia SAC.
 *
 * Duas saídas: **Fechar negociação** cria o contrato já ativo, com cronograma e
 * checklist na mesma transação (`ativarAoCriar`); **Criar como simulação**
 * grava o rascunho editável, como a tela de contratos sempre fez.
 */
function ModalDeFechamento(props: {
  readonly entrada: EntradaSimulacaoPassivo;
  readonly resultado: ResultadoSimulacaoPassivo;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly aoConcluir: (contrato: ContratoPassivo) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const clientes = useClientesParaEscolher();
  const fornecedores = useCarteira('fornecedores');

  const [form, setForm] = useState({
    clienteId: '',
    fornecedorId: '',
    diaVencimento: '10',
    observacoes: '',
    regraPagamentoTexto: '',
  });

  const definir = (campo: keyof typeof form, valor: string): void =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const leitura = useLeituraDaFormaDePagamento(
    form.regraPagamentoTexto,
    props.entrada.quantidadeParcelas,
  );
  const dia = Number(form.diaVencimento);
  const diaValido = form.diaVencimento !== '' && Number.isInteger(dia) && dia >= 1 && dia <= 31;
  const completo =
    form.clienteId !== '' &&
    form.fornecedorId !== '' &&
    diaValido &&
    formaDePagamentoValida(form.regraPagamentoTexto, leitura);

  const criar = useMutation({
    mutationFn: (ativar: boolean) => {
      const corpo: EntradaContratoPassivo = {
        clienteId: form.clienteId,
        fornecedorId: form.fornecedorId,
        valorDividaOriginal: props.entrada.valorDivida,
        // RN-201: o crédito comprado é a própria dívida; o backend recalcula os
        // derivados a partir dele e do deságio, e chega nos mesmos números.
        valorCreditoComprado: props.resultado.valorCreditoComprado,
        percentualDesagio: props.entrada.percentualDesagio,
        quantidadeParcelas: props.entrada.quantidadeParcelas,
        taxaJurosMensal: props.entrada.taxaJurosMensal,
        indice: props.entrada.indice,
        diaVencimento: dia,
        ...(form.observacoes.trim() === '' ? {} : { observacoes: form.observacoes.trim() }),
        ...(form.regraPagamentoTexto.trim() === ''
          ? {}
          : { regraPagamentoTexto: form.regraPagamentoTexto.trim() }),
        ...(ativar ? { ativarAoCriar: true } : {}),
      };

      return requisicaoAutenticada<ContratoPassivo>('/passivo/contratos', {
        metodo: 'POST',
        corpo,
      });
    },
    onSuccess: (contrato) => {
      // Pelo prefixo: a lista "Todos" da tela de Contratos também muda.
      void queryClient.invalidateQueries({ queryKey: ['passivo-contratos'] });
      void queryClient.invalidateQueries({ queryKey: ['passivo-painel'] });
      void queryClient.invalidateQueries({ queryKey: ['passivo-simulacoes'] });
      props.aoConcluir(contrato);
    },
  });

  const fechar = (): void => {
    if (criar.isPending) return;
    criar.reset();
    props.aoFechar();
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Fechar negociação"
      descricao="Escolha as partes e o dia de vencimento. Os valores são os da simulação."
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar} disabled={criar.isPending}>
            Cancelar
          </Botao>
          <Botao
            variante="secundario"
            onClick={() => criar.mutate(false)}
            disabled={!completo || criar.isPending}
            carregando={criar.isPending && criar.variables === false}
          >
            Criar como simulação
          </Botao>
          <Botao
            onClick={() => criar.mutate(true)}
            disabled={!completo || criar.isPending}
            carregando={criar.isPending && criar.variables === true}
          >
            Fechar negociação
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <ResumoDaSimulacao
          entrada={props.entrada}
          resultado={props.resultado}
          diaVencimento={diaValido ? dia : null}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeSelecao
            rotulo="Cliente"
            obrigatorio
            ajuda="A empresa que deve o ICMS."
            value={form.clienteId}
            onChange={(ev) => definir('clienteId', ev.target.value)}
            opcoes={[
              { valor: '', rotulo: clientes.isPending ? 'Carregando…' : 'Selecione…' },
              ...(clientes.data ?? []).map((c) => ({
                valor: c.businessId,
                rotulo: `${c.razaoSocial} — ${formatarCnpj(c.cnpj)}`,
              })),
            ]}
          />
          <CampoDeSelecao
            rotulo="Fornecedor do crédito"
            obrigatorio
            ajuda="A empresa que vende o crédito que abate a dívida."
            value={form.fornecedorId}
            onChange={(ev) => definir('fornecedorId', ev.target.value)}
            opcoes={[
              { valor: '', rotulo: fornecedores.isPending ? 'Carregando…' : 'Selecione…' },
              ...(fornecedores.data ?? []).map((f) => ({
                valor: f.businessId,
                rotulo: `${f.razaoSocial} — ${formatarCnpj(f.cnpj)}`,
              })),
            ]}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
          <Campo
            rotulo="Dia do vencimento"
            obrigatorio
            type="number"
            min={1}
            max={31}
            ajuda="Em mês mais curto, vence no último dia."
            value={form.diaVencimento}
            onChange={(ev) => definir('diaVencimento', ev.target.value)}
            {...(form.diaVencimento !== '' && !diaValido ? { erro: 'Entre 1 e 31' } : {})}
          />
          <CampoDeTexto
            rotulo="Observações"
            rows={2}
            maxLength={5000}
            value={form.observacoes}
            onChange={(ev) => definir('observacoes', ev.target.value)}
          />
        </div>

        <CampoDaFormaDePagamento
          valor={form.regraPagamentoTexto}
          aoMudar={(texto) => definir('regraPagamentoTexto', texto)}
          leitura={leitura}
        />

        <Aviso tom="informacao" titulo="Fechar negociação ativa o contrato na hora">
          O checklist é gerado e a edição trava. As parcelas também, no padrão; com a forma de
          pagamento descrita, elas nascem quando a transferência do crédito for marcada. Para deixar
          como rascunho editável, use <strong>Criar como simulação</strong>.
        </Aviso>

        {(clientes.isError || fornecedores.isError) && (
          <ErroDaApi
            titulo="Não foi possível carregar as empresas"
            erro={clientes.error ?? fornecedores.error}
          />
        )}

        {criar.isError && <ErroDaApi titulo="A negociação não foi fechada" erro={criar.error} />}
      </div>
    </Modal>
  );
}

function ResumoDaSimulacao(props: {
  readonly entrada: EntradaSimulacaoPassivo;
  readonly resultado: ResultadoSimulacaoPassivo;
  readonly diaVencimento: number | null;
}): JSX.Element {
  const { entrada: e, resultado: r } = props;
  const sistema = e.indice === 'price' ? 'Price' : 'SAC';

  const linhas: readonly { readonly rotulo: string; readonly valor: ReactNode }[] = [
    { rotulo: 'Dívida de ICMS', valor: <ValorFiscal valor={r.valorDivida} /> },
    { rotulo: 'Valor a pagar', valor: <ValorFiscal valor={r.valorAPagar} /> },
    { rotulo: 'Total parcelado', valor: <ValorFiscal valor={r.valorTotal} /> },
    {
      rotulo: 'Economia',
      valor: (
        <span className="inline-flex items-baseline gap-2">
          <ValorFiscal valor={r.economia} variante="economia" />
          <ValorPercentual percentual={r.percentualEconomia} tamanho="pequeno" />
        </span>
      ),
    },
    {
      rotulo: 'Parcelas',
      valor: `${e.quantidadeParcelas} × ${sistema}, ${formatarPercentual(e.taxaJurosMensal)} ao mês`,
    },
    { rotulo: 'Deságio', valor: formatarPercentual(e.percentualDesagio) },
    {
      rotulo: 'Vencimento',
      valor: props.diaVencimento === null ? '—' : `Todo dia ${props.diaVencimento}`,
    },
  ];

  return (
    <dl
      aria-label="Resumo da simulação"
      className="grid gap-x-6 gap-y-2 rounded-card border border-borda bg-superficie-base px-4 py-3 text-sm sm:grid-cols-2"
    >
      {linhas.map((l) => (
        <div key={l.rotulo} className="flex items-baseline justify-between gap-3">
          <dt className="text-conteudo-sutil">{l.rotulo}</dt>
          <dd className="text-right text-conteudo-primario">{l.valor}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatarPercentual(valor: number): string {
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
}

export const COLUNAS_DAS_PARCELAS: readonly ColunaDaPlanilha<ParcelaSimulada>[] = [
  { titulo: 'Parcela', largura: 10, valor: (p) => p.numero },
  { titulo: 'Valor', formato: 'moeda', valor: (p) => p.valor },
  { titulo: 'Amortização', formato: 'moeda', valor: (p) => p.amortizacao },
  { titulo: 'Juros', formato: 'moeda', valor: (p) => p.juros },
  { titulo: 'Saldo devedor', formato: 'moeda', valor: (p) => p.saldoDevedor },
];

const COLUNAS: readonly ColunaTabela<ParcelaSimulada>[] = [
  { chave: 'numero', titulo: 'Parcela', celula: (p) => String(p.numero) },
  {
    chave: 'valor',
    titulo: 'Valor',
    numerica: true,
    celula: (p) => <ValorFiscal valor={p.valor} />,
  },
  {
    chave: 'amortizacao',
    titulo: 'Amortização',
    numerica: true,
    celula: (p) => <ValorFiscal valor={p.amortizacao} />,
  },
  {
    chave: 'juros',
    titulo: 'Juros',
    numerica: true,
    celula: (p) => <ValorFiscal valor={p.juros} />,
  },
  {
    chave: 'saldoDevedor',
    titulo: 'Saldo devedor',
    numerica: true,
    celula: (p) => <ValorFiscal valor={p.saldoDevedor} />,
  },
];

export function TabelaDeParcelas(props: {
  readonly parcelas: readonly ParcelaSimulada[];
  readonly total: number;
}): JSX.Element {
  const juros = props.parcelas.reduce((s, p) => s + p.juros, 0);
  const amortizacao = props.parcelas.reduce((s, p) => s + p.amortizacao, 0);

  return (
    <Tabela
      legenda="Parcelas simuladas"
      densidade="compacta"
      colunas={COLUNAS}
      dados={props.parcelas}
      chaveDaLinha={(p) => String(p.numero)}
      chaveDeCabecalhoDaLinha="numero"
      alturaMaxima="32rem"
      rodape={
        <tr>
          <CelulaDeTotal cabecalhoDaLinha>Total</CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={props.total} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={amortizacao} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={juros} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <span aria-hidden="true">—</span>
          </CelulaDeTotal>
        </tr>
      }
    />
  );
}

/**
 * A simulação por e-mail. O cliente é opcional: sem ele a proposta vai sem nome
 * de empresa, para quem o operador digitar; com ele, o nome entra no e-mail e
 * os colaboradores do cliente viram opção de destinatário.
 */
function EnvioDaSimulacao(props: {
  readonly entrada: EntradaSimulacaoPassivo;
  readonly aoFechar: () => void;
}): JSX.Element {
  const clientes = useClientesParaEscolher();
  const [clienteId, setClienteId] = useState('');

  return (
    <ModalDeEnvioPorEmail
      titulo="Enviar simulação por e-mail"
      descricao="O e-mail leva a dívida, o deságio, o valor a pagar, a economia e as parcelas. O fornecedor do crédito não aparece."
      assuntoPadrao="Simulação — Créditos ICMS Passivos"
      empresaId={clienteId === '' ? null : clienteId}
      enviar={(dados) =>
        requisicaoAutenticada<ResultadoDoEnvio>('/passivo/simulacoes/email', {
          metodo: 'POST',
          corpo: { ...props.entrada, ...dados, ...(clienteId === '' ? {} : { clienteId }) },
        })
      }
      aoFechar={props.aoFechar}
    >
      <CampoDeSelecao
        rotulo="Cliente"
        ajuda="Opcional: o nome entra no e-mail e os colaboradores dele aparecem abaixo."
        value={clienteId}
        onChange={(e) => setClienteId(e.target.value)}
        opcoes={[
          { valor: '', rotulo: 'Sem cliente' },
          ...(clientes.data ?? []).map((c) => ({
            valor: c.businessId,
            rotulo: c.nomeFantasia?.trim() || c.razaoSocial,
          })),
        ]}
      />
    </ModalDeEnvioPorEmail>
  );
}
